/**
 * Custom hooks for prefetching data on hover/interaction
 */

import { useQueryClient } from "@tanstack/react-query"

import type { ChatStore } from "@/features/chat/hooks/chat-request-registry"

import { getChatSessionQueryScope } from "@/features/chat/hooks/chat-request-registry"
import { api } from "@/lib/api-client"
import { queryKeys } from "@/lib/query-keys"
import { ChatService } from "@/services/chat-service"
import { ProjectService } from "@/services/project-service"

/**
 * Hook to prefetch session messages on hover.
 *
 * `chatStore` is the store the chat list belongs to. The chat's query identity
 * is read when the prefetch runs, so a delayed hover never fetches a deleted
 * chat and writes a revived chat's own query keys (#3524).
 */
export function usePrefetchSession(chatStore: ChatStore) {
  const queryClient = useQueryClient()

  return (sessionId: string) => {
    if (!sessionId) return
    const scope = getChatSessionQueryScope(chatStore, sessionId)
    if (scope.disposed) return

    // Prefetch session messages if not already cached
    const messagesQueryKey = queryKeys.sessionMessagesQuery(sessionId, undefined, scope.incarnation)
    const cachedData = queryClient.getQueryData(messagesQueryKey)

    if (!cachedData) {
      void queryClient.prefetchQuery({
        queryKey: messagesQueryKey,
        queryFn: async () => {
          const chatService = ChatService.getInstance()
          return chatService.getMessages(sessionId)
        },
        staleTime: 1000 * 60 * 5, // Consider prefetched data fresh for 5 minutes
      })
    }
  }
}

/**
 * Hook to prefetch project data on hover
 */
export function usePrefetchProject() {
  const queryClient = useQueryClient()

  return (projectId: string, sessionType?: string) => {
    // Prefetch project data if not already cached
    const projectQueryKey = queryKeys.project(projectId)
    const cachedData = queryClient.getQueryData(projectQueryKey)

    if (!cachedData && projectId) {
      // Prefetch by getting from projects list
      void queryClient.prefetchQuery({
        queryKey: queryKeys.projects(sessionType),
        queryFn: () => ProjectService.listProjects(sessionType),
        staleTime: 1000 * 60 * 5,
      })
    }
  }
}

/**
 * Hook to prefetch workflow data on hover
 */
export function usePrefetchWorkflow() {
  const queryClient = useQueryClient()

  return (workflowId: string) => {
    const queryKey = queryKeys.workflow(workflowId)
    const cachedData = queryClient.getQueryData(queryKey)

    if (!cachedData && workflowId) {
      void queryClient.prefetchQuery({
        queryKey,
        queryFn: () => api.get(`/explorer/workflow/${workflowId}`),
        staleTime: 1000 * 60 * 5,
      })
    }
  }
}
